/* eslint react/no-multi-comp: 0 */
import React, { useMemo, useState } from 'react';
import { createBrowserHistory } from 'history';
import overrideActions from './actions/overrides';
import PropTypes from 'prop-types';

import {
    BrowserRouter as Router,
    Routes as RouterRoutes,
    Route,
    Navigate,
} from 'react-router-dom';

import PrivateContainer from './components/app-container';
import ThemeColorizer from './components/theme/ThemeColorizer';
import Overrides from './components/theme/Overrides';

import Redirect from './RedirectToClient';
import useInjectPendo from './components/hooks/useInjectPendo';

import removeClientFromPathname from './utils/removeClientFromPathname';

import connect from './utils/connect';
import {
    AUDIENCE_INSIGHTS,
    PREMIUM_COLLECTIONS,
    PREMIUM_REPORTS,
    PACING_VIEW_GOALS,
    PACING_CREATE_GOALS,
    HOME_PAGE,
    SOCIAL_MEDIA_POST_PAGE,
    INSIGHT_PAGE,
    ALL_SOCIAL_MEDIA_POSTS_PAGE,
    SOCIAL_BENCHMARKING,
    IN_STADIUM_ANALYTICS,
} from './constants/config';

import { BROADCAST } from './constants/platformIds';
import SocialBenchmarking from './SocialBenchmarking';
import AllSocialMediaPosts from './allSocialMediaPosts';

import HomePage from './home';
import InsightsPage from './insights';
import SocialPostsPage from './social-media-posts';
import BroadcastsPage from './broadcasts';
import BroadcastsTabContent from './broadcasts/components/BroadcastsTabContent';
import ExposureDetailsModal from './broadcasts/components/exposures/ExposureWrapper';
import VerbalMentionVideoModal from './broadcasts/components/verbal-mentions/VerbalMentionVideoWrapper';
import SettingsPage from './settings';
import ApiReference from './settings/views/ApiDocumentation/ApiReference';
import BareContainer from './components/app-container/BareContainer';
import ClientSettingsPage from './clientSettings/ClientSettings';
import LoginPage from './login';
import SSOLoginPage from './login/SSOLogin';
import SSOForwardPage from './login/SSOForwardPage';
import FreshdeskSSOForwardPage from './login/FreshdeskSSOForwardPage';
import CollectionsPage from './collections';
import PacingGraph from './pacing/PacingGraph';
import PacingPage from './pacing/Pacing';
import PacingCreatePage from './pacing/CreatePacing';
import ResetPasswordPage from './reset-password';
import ReportsPage from './reports';
import EmailNotificationsPage from './email-notifications/EmailNotifications';
import TwitterRedirectPage from './social/TwitterRedirect';
import UnsubscribePage from './email-notifications/Unsubscribe';
import AudiencePage from './audience';
import InVenue from './in-venue';
import RouteNames from './RouteNames';
import { QueryClientProvider } from '@tanstack/react-query';
import queryClient from './utils/queryClient';
import Notifications from './components/notifications/Notifications';
import { getUiCatalogRoutes } from './ui/catalog/routes';

const basename = `/${__BASEPATH__}`;

const history = window._history || createBrowserHistory({ basename });
if (__DEV__ && !window._history) window._history = history;

let prevPath = '';
history.listen(location => {
    if (location.pathname !== prevPath) {
        const cleanPath = removeClientFromPathname(location.pathname);
        prevPath = cleanPath;
    }
});

const Routes = ({ clearColors, client }) => {
    useInjectPendo();
    const [shouldAddPath, setAddPath] = useState(true);

    const valuationStatusId = client ? client.valuationStatusId : null;
    const showOnboardingOnly = valuationStatusId == '1';

    const _shouldRedirect = perm =>
        client &&
        client.permissions &&
        client.permissions.length > 0 &&
        !client.permissions.includes(perm);

    const shouldRedirectBroadcast =
        client &&
        Array.isArray(client.platformIds) &&
        !client.platformIds.includes(BROADCAST);
    const shouldRedirectHome = _shouldRedirect(HOME_PAGE);
    const shouldRedirectSocialMediaPost = _shouldRedirect(
        SOCIAL_MEDIA_POST_PAGE,
    );
    const shouldRedirectAllSocialMediaPosts = _shouldRedirect(
        ALL_SOCIAL_MEDIA_POSTS_PAGE,
    );
    const shouldRedirectInsight = _shouldRedirect(INSIGHT_PAGE);

    const shouldRedirectAudience = _shouldRedirect(AUDIENCE_INSIGHTS);
    const shouldRedirectCollections = _shouldRedirect(PREMIUM_COLLECTIONS);
    const shouldRedirectPacingPage = _shouldRedirect(PACING_VIEW_GOALS);
    const shouldRedirectPacingCreatePage =
        shouldRedirectPacingPage && _shouldRedirect(PACING_CREATE_GOALS);
    const shouldRedirectReports = _shouldRedirect(PREMIUM_REPORTS);

    const inPreValuation = valuationStatusId == '1';
    const shouldRedirectSocialBenchmarking =
        inPreValuation || _shouldRedirect(SOCIAL_BENCHMARKING);

    const shouldRedirectInVenue = _shouldRedirect(IN_STADIUM_ANALYTICS);

    const _redirect = () => (
        <Redirect
            shouldAddPath={shouldAddPath}
            setAddPath={setAddPath}
            client={client}
        />
    );

    const privateRoutes = useMemo(() => {
        return [
            {
                key: 'home',
                path: '/c/:clientId',
                element: <HomePage />,
                isPrivate: true,
                shouldHide: showOnboardingOnly,
                shouldRedirect: shouldRedirectHome,
            },
            {
                key: 'insights',
                path: '/c/:clientId/insights',
                element: <InsightsPage />,
                isPrivate: true,
                shouldHide: showOnboardingOnly,
                shouldRedirect: shouldRedirectInsight,
            },
            {
                key: 'social-media-posts',
                path: '/c/:clientId/social-media-posts',
                element: <SocialPostsPage />,
                isPrivate: true,
                shouldHide: showOnboardingOnly,
                shouldRedirect: shouldRedirectSocialMediaPost,
            },
            {
                key: 'all-social-media-posts',
                path: '/c/:clientId/all-social-media-posts',
                element: <AllSocialMediaPosts />,
                isPrivate: true,
                shouldHide: showOnboardingOnly,
                shouldRedirect: shouldRedirectAllSocialMediaPosts,
            },
            {
                key: RouteNames.broadcasts.key,
                path: RouteNames.broadcasts.path,
                element: <BroadcastsPage />,
                isPrivate: true,
                shouldHide: showOnboardingOnly,
                shouldRedirect: shouldRedirectBroadcast,
                children: [
                    {
                        key: RouteNames.broadcastsTabs.key,
                        path: RouteNames.broadcastsTabs.path,
                        element: <BroadcastsTabContent />,
                        isPrivate: true,
                        shouldHide: showOnboardingOnly,
                        shouldRedirect: shouldRedirectBroadcast,
                        children: [
                            {
                                key: RouteNames.broadcastModal.key,
                                path: RouteNames.broadcastModal.path,
                                element: <ExposureDetailsModal />,
                                isPrivate: true,
                                shouldHide: showOnboardingOnly,
                                shouldRedirect: shouldRedirectBroadcast,
                            },
                            {
                                key: RouteNames.verbalMentionModal.key,
                                path: RouteNames.verbalMentionModal.path,
                                element: <VerbalMentionVideoModal />,
                                isPrivate: true,
                                shouldHide: showOnboardingOnly,
                                shouldRedirect: shouldRedirectBroadcast,
                            },
                        ],
                    },
                ],
            },
            {
                key: 'reports',
                path: '/c/:clientId/reports',
                element: <ReportsPage />,
                isPrivate: true,
                shouldHide: showOnboardingOnly,
                shouldRedirect: shouldRedirectReports,
            },
            {
                key: 'social-benchmarking',
                path: '/c/:clientId/social-benchmarking',
                element: <SocialBenchmarking />,
                isPrivate: true,
                shouldHide: showOnboardingOnly,
                shouldRedirect: shouldRedirectSocialBenchmarking,
            },
            {
                key: 'audience',
                path: '/c/:clientId/audience',
                element: <AudiencePage />,
                isPrivate: true,
                shouldHide: showOnboardingOnly,
                shouldRedirect: shouldRedirectAudience,
            },
            {
                key: RouteNames.collections.key,
                path: RouteNames.collections.path,
                element: <CollectionsPage />,
                isPrivate: true,
                shouldHide: showOnboardingOnly,
                shouldRedirect: shouldRedirectCollections,
            },
            {
                key: 'pacing',
                path: '/c/:clientId/pacing',
                element: <PacingPage />,
                isPrivate: true,
                shouldHide: showOnboardingOnly,
                shouldRedirect: shouldRedirectPacingPage,
            },
            {
                key: 'pacing-create',
                path: '/c/:clientId/pacing/create',
                element: <PacingCreatePage />,
                isPrivate: true,
                shouldHide: showOnboardingOnly,
                shouldRedirect: shouldRedirectPacingCreatePage,
            },
            {
                key: 'in-venue',
                path: '/c/:clientId/in-venue',
                element: <InVenue />,
                isPrivate: true,
                shouldHide: showOnboardingOnly,
                shouldRedirect: shouldRedirectInVenue,
            },
            {
                key: RouteNames.settings.key,
                path: RouteNames.settings.path,
                element: <SettingsPage />,
                isPrivate: true,
                shouldHide: false,
                shouldRedirect: false,
            },
            {
                key: 'clientSettings',
                path: '/c/:clientId/client-configuration/*',
                element: <ClientSettingsPage />,
                isPrivate: true,
                shouldHide: false,
                shouldRedirect: false,
            },
            {
                key: 'api-documentation',
                path: '/c/:clientId/api-documentation',
                element: <ApiReference />,
                isPrivate: true,
                bareLayout: true,
                shouldHide: false,
                shouldRedirect: false,
            },
            {
                key: 'api-documentation-operation',
                path: '/c/:clientId/api-documentation/operations/:operationId',
                element: <ApiReference />,
                isPrivate: true,
                bareLayout: true,
                shouldHide: false,
                shouldRedirect: false,
            },
            {
                key: 'api-documentation-schema',
                path: '/c/:clientId/api-documentation/schemas/:schemaName',
                element: <ApiReference />,
                isPrivate: true,
                bareLayout: true,
                shouldHide: false,
                shouldRedirect: false,
            },
            {
                key: 'redirect',
                path: '*',
                element: <Navigate to={`/c/${client.id}`} />,
                isPrivate: true,
                shouldHide: false,
                shouldRedirect: false,
            },
        ];
    }, [client, showOnboardingOnly]);

    const publicRoutes = useMemo(
        () => [
            ...getUiCatalogRoutes(__DEV__),
            {
                key: RouteNames.login.key,
                path: RouteNames.login.path,
                element: <LoginPage />,
                isPrivate: false,
                shouldHide: false,
                shouldRedirect: false,
            },
            {
                key: 'ssologin',
                path: '/ssologin',
                element: <SSOLoginPage />,
                isPrivate: false,
                shouldHide: false,
                shouldRedirect: false,
            },
            {
                key: 'zendeskSSO',
                path: '/zendesk/sso',
                element: <SSOForwardPage />,
                isPrivate: false,
                shouldHide: false,
                shouldRedirect: false,
            },
            {
                key: 'freshdeskSSO',
                path: '/freshdesk/sso',
                element: <FreshdeskSSOForwardPage />,
                isPrivate: false,
                shouldHide: false,
                shouldRedirect: false,
            },
            {
                key: 'email-notifications',
                path: '/email-notifications',
                element: <EmailNotificationsPage />,
                isPrivate: false,
                shouldHide: false,
                shouldRedirect: false,
            },
            {
                key: 'unsubscribe',
                path: '/unsubscribe',
                element: <UnsubscribePage />,
                isPrivate: false,
                shouldHide: false,
                shouldRedirect: false,
            },
            {
                key: RouteNames.resetPassword.key,
                path: RouteNames.resetPassword.path,
                element: <ResetPasswordPage />,
                isPrivate: false,
                shouldHide: false,
                shouldRedirect: false,
            },
            {
                key: 'pacing-graph',
                path: '/pacing-graph/:id',
                element: <PacingGraph />,
                isPrivate: false,
                shouldHide: false,
                shouldRedirect: false,
            },
            {
                key: 'authentication-twitter',
                path: '/authentication/twitter',
                element: <TwitterRedirectPage />,
                isPrivate: false,
                shouldHide: false,
                shouldRedirect: false,
            },
        ],
        [],
    );

    const generatePrivateRouteChildren = routes => {
        return routes
            .filter(r => !r.shouldHide)
            .map(route => {
                if (route.children && route.children.length > 0) {
                    return (
                        <Route
                            key={route.key}
                            path={route.path}
                            element={route.element}
                            shouldRedirect={route.shouldRedirect}
                            _redirect={_redirect}
                        >
                            {generatePrivateRouteChildren(route.children)}
                        </Route>
                    );
                }
                return (
                    <Route
                        key={route.key}
                        path={route.path}
                        element={route.element}
                        shouldRedirect={route.shouldRedirect}
                        _redirect={_redirect}
                    />
                );
            });
    };

    return (
        <QueryClientProvider client={queryClient}>
            <Router history={history} onLeave={clearColors} basename={basename}>
                <RouterRoutes>
                    {publicRoutes.map(route => (
                        <Route
                            key={route.key}
                            path={route.path}
                            element={
                                <>
                                    {route.element}
                                    <Notifications />
                                </>
                            }
                        />
                    ))}

                    {privateRoutes
                        .filter(r => !r.shouldHide)
                        .map(route => {
                            // Bare-layout routes skip the app sidebar/header chrome.
                            const Container = route.bareLayout
                                ? BareContainer
                                : PrivateContainer;
                            if (route.children && route.children.length > 0) {
                                return (
                                    <Route
                                        key={route.key}
                                        path={route.path}
                                        element={
                                            <Container>
                                                {route.element}
                                            </Container>
                                        }
                                        shouldRedirect={route.shouldRedirect}
                                        _redirect={_redirect}
                                    >
                                        {generatePrivateRouteChildren(
                                            route.children,
                                        )}
                                    </Route>
                                );
                            } else {
                                return (
                                    <Route
                                        key={route.key}
                                        path={route.path}
                                        element={
                                            <Container>
                                                {route.element}
                                            </Container>
                                        }
                                        shouldRedirect={route.shouldRedirect}
                                        _redirect={_redirect}
                                    />
                                );
                            }
                        })}
                </RouterRoutes>

                <ThemeColorizer />
                <Overrides />
            </Router>
        </QueryClientProvider>
    );
};

Routes.propTypes = {
    client: PropTypes.object.isRequired,
    clearColors: PropTypes.func.isRequired,
};

export default connect(['client'], overrideActions)(Routes);
